import { createSelector } from '@reduxjs/toolkit';
import { INTEGRATIONS } from 'config/integrationsConfig';
import { PRODUCTS } from 'config/productsConfig';
import { CONSTANTS_SETTINGS } from 'config/settingsContainer';

//📌 Selector for get all settings from current profile.
export const selectCurrentProfileOrgSettings = createSelector(
	[(state) => state.Settings.mergedOrgSettings, (state) => state.Auth.selectedProfile.orgID],
	(settings, orgID) => settings[orgID] || []
);

// Selector for get all person settings from current profile.
export const selectCurrentProfilePersonSettings = createSelector(
	[(state) => state.Settings.mergedPersonSettings, (state) => state.Auth.selectedProfile.personID],
	(settings, personID) => settings[personID] || []
);

// Note: “is Access Module enabled?” (defaultSettingID 82)
export const selectHasAccessModule = createSelector([selectCurrentProfileOrgSettings], (orgSettings) =>
	orgSettings.some((s) => s.defaultSettingID === CONSTANTS_SETTINGS.ACCESS_MODULE && Number(s.value) === 1)
);

//  Note: “is Locker Module enabled?” (defaultSettingID 83)
export const selectHasLockerModule = createSelector([selectCurrentProfileOrgSettings], (orgSettings) =>
	orgSettings.some((s) => s.defaultSettingID === CONSTANTS_SETTINGS.LOCKER_MODULE && Number(s.value) === 1)
);

// Generic sync status selector - returns true if ANY integration is syncing (IR or Control)
export const selectSyncInProgress = createSelector([selectCurrentProfileOrgSettings], (orgSettings) => {
	const syncingSettings = [CONSTANTS_SETTINGS.IR_SETTING_SYNCING, CONSTANTS_SETTINGS.CONTROL_SETTING_SYNCING];
	return syncingSettings.some(
		(settingID) => orgSettings.find((s) => s.defaultSettingID === settingID)?.value === '1'
	);
});

// Legacy selector for IR sync (for backwards compatibility)
export const selectIRSyncInProgress = createSelector(
	[selectCurrentProfileOrgSettings],
	(orgSettings) =>
		orgSettings.find((s) => s.defaultSettingID === CONSTANTS_SETTINGS.IR_SETTING_SYNCING)?.value === '1'
);

// Control sync status selector
export const selectControlSyncInProgress = createSelector(
	[selectCurrentProfileOrgSettings],
	(orgSettings) =>
		orgSettings.find((s) => s.defaultSettingID === CONSTANTS_SETTINGS.CONTROL_SETTING_SYNCING)?.value === '1'
);

export const selectIsModuleEnabled = (defaultSettingID) =>
	createSelector([selectCurrentProfileOrgSettings], (orgSettings) =>
		orgSettings.some((s) => s.defaultSettingID === defaultSettingID && Number(s.value) === 1)
	);

// Use this when you have a list of things each with a `moduleFlag` and need
// "the subset that's active". For a single flag, use selectIsModuleEnabled.
export const selectActiveModules = (modules) =>
	createSelector([selectCurrentProfileOrgSettings], (orgSettings) =>
		modules.filter((m) => {
			const row = orgSettings.find((s) => s.defaultSettingID === m.moduleFlag);
			return Number(row?.value) === 1;
		})
	);

// ── Concrete selectors ──────────────────────────────────────────────────────
export const selectActiveProducts = selectActiveModules(PRODUCTS);
export const selectActiveIntegrations = selectActiveModules(INTEGRATIONS);
